import { useMemo } from 'react';
import { SteppedDialogFormProps, useSteppedDialogForm, useSteppedDialogFormControls } from '@mews-commander/form';
import { createExperimentalZodFormResolver, lt } from '@mews/framework';
import { OptimusProvider } from '@mews-commander/ui';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { Banner, Stack } from '@optimus-web/core';
import { UpdateEnterpriseProductPricingFormStep } from '../LocalProductSidePanel/forms/types';
import { TaxationMultiplePropertiesForm } from '../ConfigureProductForm/steps/taxation/TaxationMultiplePropertiesForm';
import { PricingMultiplePropertiesForm } from '../ConfigureProductForm/steps/pricing/PricingMultiplePropertiesForm';
import { PricingTableData } from '../../types';
import { BulkUpdateEnterpriseProductFormValues } from './schemas/bulkUpdateEnterpriseProductFormSchema';
import { taxesSchema } from './schemas/configureProductFormSchema';

interface Props {
    centralProductId: string;
    enterpriseIds: string[];
}

export const useBulkUpdateEnterpriseProductPricingDialogForm = ({ centralProductId, enterpriseIds }: Props) =>
    useSteppedDialogForm<UpdateEnterpriseProductPricingFormStep>({
        id: 'bulkUpdateEnterpriseProductPricingForm',
        title: lt('EnterpriseProductBulkEditPricingFormTitle'),
        steps: ['taxes', 'pricing'],
        saveLabel: lt('Update'),
        stepLabels: {
            taxes: lt('ConfigureProductStepperTaxes'),
            pricing: lt('Pricing'),
        },
        renderForm: (props) => (
            <OptimusProvider density="spacious">
                <BulkUpdateEnterpriseProductPricingFormContent
                    {...props}
                    centralProductId={centralProductId}
                    enterpriseIds={enterpriseIds}
                />
            </OptimusProvider>
        ),
    });

const BulkUpdateEnterpriseProductPricingFormContent = ({
    centralProductId,
    enterpriseIds,
    ...props
}: SteppedDialogFormProps<UpdateEnterpriseProductPricingFormStep> & Props) => {
    const {
        data: { taxes, basePrices },
        isFetching,
    } = useRepositorySuspenseQuery('portfolioEnterpriseProducts', 'getBasePrices', {
        centralProductId,
        propertyIds: enterpriseIds,
    });

    const tableData = useMemo(
        () =>
            basePrices.data.map(({ amount, city, country, property }) => {
                return {
                    id: property.id,
                    countryName: country,
                    cityName: city,
                    defaultCurrencyCode: amount.currency,
                    name: property.name,
                };
            }),
        [basePrices]
    ) satisfies PricingTableData[];

    const defaultValues = useMemo(() => {
        return {
            pricing: {
                ...basePrices.data.reduce((acc, { property, amount }) => {
                    return {
                        ...acc,
                        [property.id]: {
                            price: amount.netValue,
                            currencyCode: amount.currency,
                        },
                    };
                }, {}),
            },
            taxEnvironmentCode: taxes.data.reduce(
                (acc, { taxEnvironmentCode, taxationCode, taxRates }) => ({
                    ...acc,
                    [taxEnvironmentCode]: {
                        taxRate: {
                            [taxationCode]: taxRates?.length === 1 ? taxRates[0] : '',
                        },
                    },
                }),
                {}
            ),
        };
    }, [taxes, basePrices]);

    const { renderFormContent } = useSteppedDialogFormControls<
        UpdateEnterpriseProductPricingFormStep,
        BulkUpdateEnterpriseProductFormValues
    >({
        ...props,
        defaultValues,
        stepResolvers: {
            taxes: createExperimentalZodFormResolver(taxesSchema),
        },
        onSubmit: async () => {},
    });

    return (
        <Stack vertical spacing="300">
            <Banner
                variant="info"
                secondaryText={lt('EnterpriseProductEditPricingFormApplyNewPriceFutureInfoMessage')}
            />
            {renderFormContent((step) => {
                switch (step) {
                    case 'taxes':
                        return (
                            <TaxationMultiplePropertiesForm
                                title={lt('EnterpriseProductEditPricingFormTaxesTitle')}
                                description={lt('EnterpriseProductBulkEditPricingFormTaxesStepDescription')}
                                taxEnvironmentCodes={taxes.data.map(({ taxEnvironmentCode }) => taxEnvironmentCode)}
                            />
                        );
                    case 'pricing':
                        return <PricingMultiplePropertiesForm isFetching={isFetching} tableData={tableData} />;
                }
            })}
        </Stack>
    );
};
